import { reactive } from 'vue';

/**
 * CONFIRM — the game's "are you sure?" rail, same pattern as `toast()`.
 * Any spend/sell/fight handler calls `confirmAction({...})` and awaits a
 * boolean; the single ConfirmHost (mounted once in HeroScreen, like ToastHost)
 * renders a deco calling-card dialog — the same visual language as the
 * pre-fight confirmation on the City Map.
 */
export interface ConfirmRow {
  label: string;
  value: string;
  /** Optional leading image (currency icon, item art…). */
  icon?: string;
  tone?: 'gold' | 'bright' | 'error' | 'success' | 'dim';
}

export interface ConfirmOpts {
  /** Small overline, e.g. "The Tradestead" / "The Weighing Stone". */
  kicker?: string;
  /** The question — "Buy Chipped Hand-Axe?" */
  title: string;
  /** Optional lead image (item art, pack art). */
  icon?: string;
  /** Cost/effect breakdown rows. */
  rows?: ConfirmRow[];
  /** Italic footnote (e.g. "Real-money purchase — test mode"). */
  note?: string;
  confirmLabel?: string; // default "Confirm"
  cancelLabel?: string;  // default "Walk away"
  /** Red accent for destructive/irreversible actions. */
  danger?: boolean;
  /** Real-money purchase: the player must tick the Terms + Privacy consent
   *  before confirming, and the accepted payment methods are listed
   *  (CheckoutTerms.vue). */
  checkout?: boolean;
}

interface ConfirmState {
  open: boolean;
  opts: ConfirmOpts | null;
}

export const confirmState = reactive<ConfirmState>({ open: false, opts: null });

let resolver: ((v: boolean) => void) | null = null;

/** Ask the player to confirm. Resolves false on cancel/backdrop/Escape. */
export function confirmAction(opts: ConfirmOpts): Promise<boolean> {
  resolver?.(false); // a newer ask replaces any dangling one
  confirmState.open = true;
  confirmState.opts = opts;
  return new Promise((res) => { resolver = res; });
}

/** Called by ConfirmHost when the player picks a side. */
export function settleConfirm(v: boolean): void {
  confirmState.open = false;
  confirmState.opts = null;
  resolver?.(v);
  resolver = null;
}
